import React, { useState, useEffect } from 'react';
import { Box, ThemeProvider, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SignupForm } from './SignupForm';
import { theme } from '../../style/theme';
import signupBackground from '../../Images/signup.png';
import exportimg from '../../Images/extract.png';
import loadimg from '../../Images/load.png';
import transformimg from '../../Images/transform.png';
import AnimatedOverlayShowcase from '../../components/AnimatedOverlayShowcase';

export function SignupPage() {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e) => {
      const leftPanel = document.getElementById('signup-left-panel');
      if (leftPanel) {
        const rect = leftPanel.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      }
    };

    const leftPanel = document.getElementById('signup-left-panel');
    if (leftPanel) {
      leftPanel.addEventListener('mousemove', handleMouseMove);
      return () => leftPanel.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          backgroundColor: '#f5f5f5',
        }}
      >
        {/* Left Panel - Grid Animation and Branding */}
        <Box
          id="signup-left-panel"
          sx={{
            flex: 1,
            background: '#3044A9',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'none',
          }}
        >
          {/* Enhanced Grid Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              opacity: 0.25,
            }}
          />

          {/* Cursor Following Grid Highlight */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Fine Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              opacity: 0.2,
            }}
          />

          {/* Cursor Following Fine Grid */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Cursor Glow Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
              transition: 'background 0.2s ease-out',
              pointerEvents: 'none',
            }}
          />

          {/* Animated Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
                radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
              `,
              animation: 'gridPulse 8s ease-in-out infinite',
              '@keyframes gridPulse': {
                '0%, 100%': {
                  opacity: 0.3,
                },
                '50%': {
                  opacity: 0.6,
                },
              },
            }}
          />

          {/* Depth and Glow Effects */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: `
                radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
                linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
                linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
              `,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '10%',
                left: '10%',
                right: '10%',
                bottom: '10%',
                background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
              },
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0px) scale(1)',
                  opacity: 0.3,
                },
                '50%': {
                  transform: 'translateY(-10px) scale(1.05)',
                  opacity: 0.5,
                },
              },
            }}
          />

          {/* SkyShift Logo at Top Left */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              p: 4,
            }}
          >
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                width: 'fit-content',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
             
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { md: '2rem', lg: '3rem' },
                  background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: 'left',
                }}
              >
                SkyShift
              </Typography>
            </Box>
          </Box>

          {/* Main Content - Centered */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              maxWidth: '600px',
              px: 4,
              gap: 4,
              height: '100%',
            }}
          >
            {/* Text content */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { md: '2rem', lg: '2.5rem' },
                  background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Get Started Quickly
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { md: '1.1rem', lg: '1.25rem' },
                }}
              >
                Set up your workflows in minutes: extract, transform, and load data with ease.
              </Typography>
            </Box>

            {/* Animated showcase below text */}
            <AnimatedOverlayShowcase
              backgroundSrc={signupBackground}
              layers={[
                {
                  src: exportimg,
                  alt: 'Export',
                  from: 'bottom',
                  style: {
                    top: '30%',
                    left: '-10%',
                    width: '20%',
                    transform: 'translateX(0)'
                  },
                },
                {
                  src: transformimg,
                  alt: 'Transform',
                  from: 'bottom',
                  style: {
                    bottom: '10%',
                    left: '-10%',
                    width: '20%',
                    transform: 'translateX(0)'
                  },
                },
                {
                  src: loadimg,
                  alt: 'Load',
                  from: 'bottom',
                  style: {
                    top: '15%',
                    right: '-10%',
                    width: '20%',
                    transform: 'translateX(0)'
                  },
                },
              ]}
            />
          </Box>
        </Box>

        {/* Right Panel - Signup Form */}
        <Box
          sx={{
            flex: { xs: 1, md: 0.6 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            p: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: '400px',
              p: { xs: 3, sm: 4 },
              backgroundColor: 'transparent',
            }}
          >
            {/* Signup Form Content */}
            <SignupForm />
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default SignupPage;
